/**
 * ConnectorSourcePicker — Two-phase inline component for selecting tables from connectors.
 * Phase 1: Searchable list of connectors (project + tenant-wide).
 * Phase 2: TableBrowser for table/column selection with preview.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  CloudUpload as CloudIcon,
  ArrowBack as BackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  listConnectors,
  discoverTables,
  previewTable,
} from '../../services/connectorApi';
import type { ConnectorConfig, DiscoveredTable } from '../../services/connectorApi';
import TableBrowser from '../common/TableBrowser';
import type { TableEntry } from '../common/TableBrowser';

export interface ConnectorSourceEntry {
  key: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  tableName: string;
  schemaName?: string;
  selectedColumns?: string[];
}

const CONNECTOR_COLORS: Record<string, string> = {
  postgres: '#336791',
  mysql: '#4479a1',
  mssql: '#cc2927',
  snowflake: '#29b5e8',
  bigquery: '#4285f4',
  redshift: '#8c4fff',
  s3: '#ff9900',
  gcs: '#4285f4',
  filesystem: '#64748b',
};

const PAGE_SIZE = 50;

interface ConnectorSourcePickerProps {
  projectId: string;
  onAddSources: (entries: ConnectorSourceEntry[]) => void;
}

const ConnectorSourcePicker: React.FC<ConnectorSourcePickerProps> = ({
  projectId,
  onAddSources,
}) => {
  // Phase state
  const [selectedConnector, setSelectedConnector] = useState<ConnectorConfig | null>(null);

  // Connector list
  const [allConnectors, setAllConnectors] = useState<(ConnectorConfig & { isProjectScoped: boolean })[]>([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [connectorSearch, setConnectorSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // TableBrowser state
  const [tables, setTables] = useState<TableEntry[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [selectedColumns, setSelectedColumns] = useState<Map<string, string[]>>(new Map());
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Fetch connectors (project-scoped + tenant-wide)
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoadingConnectors(true);
      setError(null);
      try {
        const [projectConns, allConns] = await Promise.all([
          listConnectors({ project_id: projectId, direction: 'source' }),
          listConnectors({ direction: 'source' }),
        ]);
        if (cancelled) return;

        const projectIds = new Set(projectConns.map((c) => c.connector_id));
        const merged: (ConnectorConfig & { isProjectScoped: boolean })[] = [];
        const seen = new Set<string>();

        // Project connectors first
        for (const c of projectConns) {
          if (!seen.has(c.connector_id)) {
            seen.add(c.connector_id);
            merged.push({ ...c, isProjectScoped: true });
          }
        }
        // Then tenant-wide (not already in project)
        for (const c of allConns) {
          if (!seen.has(c.connector_id)) {
            seen.add(c.connector_id);
            merged.push({ ...c, isProjectScoped: false });
          }
        }
        setAllConnectors(merged);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load connectors');
      } finally {
        if (!cancelled) setLoadingConnectors(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [projectId]);

  const filteredConnectors = useMemo(() => {
    if (!connectorSearch) return allConnectors;
    const q = connectorSearch.toLowerCase();
    return allConnectors.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.connector_type.toLowerCase().includes(q),
    );
  }, [allConnectors, connectorSearch]);

  // Fetch tables for selected connector
  const fetchTables = useCallback(
    async (connectorId: string, searchStr: string, pageNum: number) => {
      setTablesLoading(true);
      try {
        const resp = await discoverTables({
          connectorId,
          search: searchStr || undefined,
          includeColumns: false,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
        const connector = allConnectors.find(c => c.connector_id === connectorId);
        const selectedStreams = connector?.config?._selected_streams;
        let tablesList = resp.tables || [];
        if (selectedStreams?.length) {
          tablesList = tablesList.filter(t => selectedStreams.includes(t.name));
        }
        const entries: TableEntry[] = tablesList.map(
          (t: DiscoveredTable): TableEntry => ({
            name: t.name,
            schemaName: t.schema_name || undefined,
            rowCountEstimate: t.row_count_estimate ?? undefined,
            columnCount: t.column_count ?? undefined,
          }),
        );
        setTables(entries);
        setTotalCount(tablesList.length);
      } catch {
        setTables([]);
        setTotalCount(0);
      } finally {
        setTablesLoading(false);
      }
    },
    [allConnectors],
  );

  // When connector changes, fetch first page
  useEffect(() => {
    if (!selectedConnector) {
      setTables([]);
      setTotalCount(0);
      setSelectedTables(new Set());
      setSelectedColumns(new Map());
      return;
    }
    setSearch('');
    setPage(1);
    fetchTables(selectedConnector.connector_id, '', 1);
  }, [selectedConnector, fetchTables]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        if (selectedConnector) {
          setPage(1);
          fetchTables(selectedConnector.connector_id, value, 1);
        }
      }, 300);
    },
    [selectedConnector, fetchTables],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      if (selectedConnector) {
        fetchTables(selectedConnector.connector_id, search, newPage);
      }
    },
    [selectedConnector, search, fetchTables],
  );

  const handleExpandTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnector) return;
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, columnsLoading: true } : t)),
      );
      try {
        const resp = await discoverTables({
          connectorId: selectedConnector.connector_id,
          search: tableName,
          includeColumns: true,
          page: 1,
          pageSize: 1,
        });
        const match = resp.tables?.find((t: DiscoveredTable) => t.name === tableName);
        if (match?.columns) {
          setTables((prev) =>
            prev.map((t) =>
              t.name === tableName
                ? {
                    ...t,
                    columns: match.columns!.map((c) => ({
                      name: c.name,
                      dtype: c.dtype,
                      nullable: c.nullable,
                    })),
                    columnCount: match.columns!.length,
                    columnsLoading: false,
                  }
                : t,
            ),
          );
          setSelectedColumns((prev) => {
            const next = new Map(prev);
            if (!next.has(tableName)) {
              next.set(tableName, match.columns!.map((c) => c.name));
            }
            return next;
          });
        } else {
          setTables((prev) =>
            prev.map((t) => (t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t)),
          );
        }
      } catch {
        setTables((prev) =>
          prev.map((t) => (t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t)),
        );
      }
    },
    [selectedConnector],
  );

  const handlePreviewTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnector) return;
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, previewLoading: true } : t)),
      );
      try {
        const tbl = tables.find((t) => t.name === tableName);
        const cols = selectedColumns.get(tableName);
        const resp = await previewTable({
          connectorId: selectedConnector.connector_id,
          tableName,
          schemaName: tbl?.schemaName,
          columns: cols && cols.length > 0 ? cols : undefined,
          limit: 25,
        });
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName
              ? { ...t, previewRows: resp.rows, previewLoading: false }
              : t,
          ),
        );
      } catch {
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName ? { ...t, previewRows: [], previewLoading: false } : t,
          ),
        );
      }
    },
    [selectedConnector, tables, selectedColumns],
  );

  const handleColumnSelectionChange = useCallback((table: string, cols: string[]) => {
    setSelectedColumns((prev) => {
      const next = new Map(prev);
      next.set(table, cols);
      return next;
    });
  }, []);

  const handleAddTables = () => {
    if (!selectedConnector || selectedTables.size === 0) return;
    const entries: ConnectorSourceEntry[] = Array.from(selectedTables).map((tableName) => {
      const tbl = tables.find((t) => t.name === tableName);
      const cols = selectedColumns.get(tableName);
      return {
        key: `${selectedConnector.connector_id}::${tableName}`,
        connectorId: selectedConnector.connector_id,
        connectorName: selectedConnector.name,
        connectorType: selectedConnector.connector_type,
        tableName,
        schemaName: tbl?.schemaName,
        selectedColumns: cols,
      };
    });
    onAddSources(entries);
    // Reset selection but stay on connector for easy adding of more
    setSelectedTables(new Set());
    setSelectedColumns(new Map());
    setSelectedConnector(null);
  };

  const connectorColor = (type: string) => CONNECTOR_COLORS[type] || '#64748b';
  const connectorIcon = (type: string) => {
    if (['s3', 'gcs', 'filesystem'].includes(type)) {
      return <CloudIcon sx={{ fontSize: 18 }} />;
    }
    return <DatabaseIcon sx={{ fontSize: 18 }} />;
  };

  // Phase 2: Table browser for selected connector
  if (selectedConnector) {
    const color = connectorColor(selectedConnector.connector_type);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Compact connector header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: alpha(color, 0.04),
            border: 1,
            borderColor: color,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 0.75,
              bgcolor: alpha(color, 0.1),
              color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {connectorIcon(selectedConnector.connector_type)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }} noWrap>
              {selectedConnector.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {selectedConnector.connector_type}
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<BackIcon sx={{ fontSize: 14 }} />}
            onClick={() => setSelectedConnector(null)}
            sx={{ textTransform: 'none', fontSize: 11, px: 1, minWidth: 'auto' }}
          >
            Back
          </Button>
        </Box>

        {/* TableBrowser */}
        <Box sx={{ minHeight: 300, maxHeight: 420, overflow: 'auto' }}>
          <TableBrowser
            tables={tables}
            loading={tablesLoading}
            totalCount={totalCount}
            searchValue={search}
            onSearchChange={handleSearchChange}
            selectionMode="multi"
            selectedTables={selectedTables}
            onSelectionChange={setSelectedTables}
            columnSelectionEnabled
            selectedColumns={selectedColumns}
            onColumnSelectionChange={handleColumnSelectionChange}
            onExpandTable={handleExpandTable}
            onPreviewTable={handlePreviewTable}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </Box>

        {/* Add button */}
        <Button
          variant="contained"
          onClick={handleAddTables}
          disabled={selectedTables.size === 0}
          sx={{ alignSelf: 'flex-end', textTransform: 'none' }}
        >
          Add {selectedTables.size} table{selectedTables.size !== 1 ? 's' : ''}
        </Button>
      </Box>
    );
  }

  // Phase 1: Connector list
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {loadingConnectors && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loadingConnectors && allConnectors.length === 0 && !error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <DatabaseIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No source connectors available
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Set up a connector in Settings
          </Typography>
        </Box>
      )}

      {!loadingConnectors && allConnectors.length > 0 && (
        <>
          {allConnectors.length > 5 && (
            <TextField
              size="small"
              fullWidth
              placeholder="Search connectors..."
              value={connectorSearch}
              onChange={(e) => setConnectorSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
            {filteredConnectors.map((conn) => {
              const color = connectorColor(conn.connector_type);
              return (
                <Card
                  key={conn.connector_id}
                  variant="outlined"
                  onClick={() => setSelectedConnector(conn)}
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': {
                      borderColor: alpha(color, 0.5),
                      bgcolor: alpha(color, 0.02),
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(color, 0.1),
                          color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {connectorIcon(conn.connector_type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }} noWrap>
                          {conn.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {conn.connector_type}
                        </Typography>
                      </Box>
                      <Chip
                        label={conn.isProjectScoped ? 'Project' : 'Shared'}
                        size="small"
                        color={conn.isProjectScoped ? 'primary' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                      <Chip
                        label={conn.status}
                        size="small"
                        color={conn.status === 'active' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18, textTransform: 'capitalize' }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ConnectorSourcePicker;
